import React, { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { FaCheck, FaTimes, FaSync } from 'react-icons/fa';
import api from '../../../services/api';
import { fmt, fmtM, fmtDate, today, yearStart } from '../utils/helpers';
import { TableExportBar } from '../shared/index';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';

const PAGE_SIZE = 25;

const TYPE_CATS = [
  { test: l => l.startsWith('Transfert'),   prefix: '⇄', color: '#1565c0', bg: '#e3f2fd', border: '#90caf9' },
  { test: l => l.startsWith('Désistement'), prefix: '✕', color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
  { test: l => l.startsWith('Livraison'),   prefix: '✓', color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
  { test: l => /Signature|Signé/.test(l),   prefix: '✍', color: '#e65100', bg: '#fff3e0', border: '#ffcc80' },
  { test: l => l.startsWith('Encaissement'),prefix: '€', color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
];
const getTypeCat = (label) => TYPE_CATS.find(c => c.test(label)) || { prefix: '', color: '#546e7a', bg: '#eceff1', border: '#b0bec5' };

const TH = { padding: '7px 10px', fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: '#4a505c', background: '#f5f4f2', borderBottom: '2px solid #d4c8bc', whiteSpace: 'nowrap' };
const TD = { padding: '7px 10px', fontSize: '0.76rem', color: '#333', borderBottom: '1px solid #ede9e4', verticalAlign: 'middle' };

const KpiCard = ({ label, value, color }) => (
  <div style={{ flex: '1 1 160px', background: '#fff', border: '2px solid #d4c8bc', borderRadius: 8, padding: '10px 14px', borderTop: `3px solid ${color}` }}>
    <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: '1rem', fontWeight: 700, color }}>{value}</div>
  </div>
);

const CancelBadge = ({ isCancelled }) => isCancelled
  ? <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#c62828', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 10, padding: '2px 7px' }}>Annulé</span>
  : <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#2e7d32', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 10, padding: '2px 7px' }}>Actif</span>;

const inputSt = { height: 32, border: '1.5px solid #d4c8bc', borderRadius: 6, padding: '0 8px', fontSize: '0.78rem', color: '#333', background: '#fff', outline: 'none' };

const CommissionSection = ({ projetIds = [], comId = null }) => {
  /* ── dates locales ── */
  const [inputDebut,   setInputDebut]   = useState(yearStart);
  const [inputFin,     setInputFin]     = useState(today);
  const [appliedDebut, setAppliedDebut] = useState(yearStart);
  const [appliedFin,   setAppliedFin]   = useState(today);

  /* ── données ── */
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  /* ── filtres UI ── */
  const [cancelFilter, setCancelFilter] = useState(null);
  const [odFilter,     setOdFilter]     = useState(null);
  const [typeFilter,   setTypeFilter]   = useState(new Set());
  const [projetFilter, setProjetFilter] = useState(new Set());
  const [comFilter,    setComFilter]    = useState(new Set());
  const [viewMode,     setViewMode]     = useState('resume');
  const [sortKey,      setSortKey]      = useState('createdAt');
  const [sortDir,      setSortDir]      = useState(-1);
  const [page,         setPage]         = useState(1);

  /* ── fetch ── */
  useEffect(() => {
    setLoading(true);
    setError('');
    const params = { debut: appliedDebut, fin: appliedFin };
    if (projetIds.length) params.projetIds = projetIds.join(',');
    if (comId)            params.comId     = comId;
    api.get('/decisionnel/commissions', { params })
      .then(r => setRows(r.data))
      .catch(e => { if (e?.response?.status !== 401) setError('Erreur chargement commissions'); })
      .finally(() => setLoading(false));
  }, [appliedDebut, appliedFin, projetIds.join(','), comId]); // eslint-disable-line

  const applyDates = () => { setAppliedDebut(inputDebut); setAppliedFin(inputFin); setPage(1); };

  /* ── pipeline filtres ── */
  const cancelFiltered = useMemo(() => {
    if (!cancelFilter) return rows;
    return rows.filter(r => cancelFilter === 'actif' ? !r.isCancelled : r.isCancelled);
  }, [rows, cancelFilter]);

  const odFiltered = useMemo(() => {
    if (!odFilter) return cancelFiltered;
    return cancelFiltered.filter(r => odFilter === 'od' ? r.isOD : !r.isOD);
  }, [cancelFiltered, odFilter]);

  const typeFiltered = useMemo(() => {
    if (typeFilter.size === 0) return odFiltered;
    return odFiltered.filter(r => typeFilter.has(r.type));
  }, [odFiltered, typeFilter]);

  const allProjets = useMemo(() => [...new Set(rows.map(r => r.projet || '—'))].sort(), [rows]);

  const projetFiltered = useMemo(() => {
    if (projetFilter.size === 0) return typeFiltered;
    return typeFiltered.filter(r => projetFilter.has(r.projet || '—'));
  }, [typeFiltered, projetFilter]);

  const filtered = useMemo(() => {
    if (comFilter.size === 0) return projetFiltered;
    return projetFiltered.filter(r => comFilter.has(r.comId));
  }, [projetFiltered, comFilter]);

  /* ── KPI ── */
  const kpi = useMemo(() => {
    const actives = filtered.filter(r => !r.isCancelled);
    return {
      nb:             filtered.length,
      commission:     actives.reduce((s, r) => s + Number(r.commission || 0), 0),
      commissionInit: actives.reduce((s, r) => s + Number(r.commission_init || 0), 0),
      nbAnnule:       filtered.filter(r => r.isCancelled).length,
    };
  }, [filtered]);

  /* ── chips ── */
  const typeChips = useMemo(() => {
    const map = {};
    odFiltered.forEach(r => {
      const k = r.type || '—';
      if (!map[k]) map[k] = { type: k, label: r.description || r.type || '—', count: 0 };
      map[k].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [odFiltered]);

  const projetChips = useMemo(() => {
    const map = {};
    allProjets.forEach(nom => { map[nom] = { nom, count: 0 }; });
    typeFiltered.forEach(r => { const k = r.projet || '—'; if (map[k]) map[k].count++; });
    return Object.values(map).filter(p => p.count > 0).sort((a, b) => b.count - a.count);
  }, [allProjets, typeFiltered]);

  const comChips = useMemo(() => {
    const map = {};
    projetFiltered.forEach(r => {
      const k = r.comId;
      if (!map[k]) map[k] = { comId: k, label: r.commercial_nom || `#${k}`, count: 0 };
      map[k].count++;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [projetFiltered]);

  /* ── résumé par commercial ── */
  const resumeRows = useMemo(() => {
    const map = {};
    filtered.forEach(r => {
      const k = r.comId || 'none';
      if (!map[k]) map[k] = { comId: k, commercial: r.commercial_nom || '—', nb: 0, commission: 0, commissionInit: 0, nbActif: 0, nbAnnule: 0 };
      map[k].nb++;
      if (!r.isCancelled) { map[k].commission += Number(r.commission || 0); map[k].commissionInit += Number(r.commission_init || 0); map[k].nbActif++; }
      else map[k].nbAnnule++;
    });
    return Object.values(map).sort((a, b) => b.commission - a.commission);
  }, [filtered]);

  /* ── tri + pagination ── */
  const NUMERIC_FIELDS = new Set(['commission','commission_init','prix_net','enc_montant','encaissementPrice','id','venteId']);
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      if (va == null && vb == null) return 0;
      if (va == null) return 1; if (vb == null) return -1;
      if (typeof va === 'string') return sortDir * va.localeCompare(vb, 'fr');
      return sortDir * (va - vb);
    });
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pagedRows  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const onSort = (k) => {
    setSortKey(prev => { if (prev === k) setSortDir(d => -d); else setSortDir(NUMERIC_FIELDS.has(k) ? -1 : 1); return k; });
    setPage(1);
  };
  const ThS = ({ label, field, right }) => (
    <th onClick={() => onSort(field)} style={{ ...TH, textAlign: right ? 'right' : 'left', cursor: 'pointer', color: sortKey === field ? '#264061' : '#4a505c' }}>
      {label} <span style={{ fontSize: '0.55rem', opacity: sortKey === field ? 1 : 0.25 }}>{sortKey === field ? (sortDir > 0 ? '▲' : '▼') : '⇅'}</span>
    </th>
  );
  const toggleSet = (setter, val) => { setter(prev => { const next = new Set(prev); next.has(val) ? next.delete(val) : next.add(val); return next; }); setPage(1); };

  return (
    <div>
      {/* ── Filtre dates local ── */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', background: '#f9f8f7', border: '1.5px solid #d4c8bc', borderRadius: 8, padding: '10px 14px' }}>
        <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#4a505c' }}>Période :</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa' }}>Du</span>
          <input type="date" value={inputDebut} onChange={e => setInputDebut(e.target.value)}
            style={inputSt} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa' }}>Au</span>
          <input type="date" value={inputFin} onChange={e => setInputFin(e.target.value)}
            style={inputSt} />
        </div>
        <button onClick={applyDates}
          style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#264061', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 14px', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}>
          <FaSync size={10} /> Appliquer
        </button>
        {loading && <Spinner size="sm" />}
        {error && <span style={{ fontSize: '0.7rem', color: '#c62828' }}>{error}</span>}
        <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#aaa' }}>
          {appliedDebut} → {appliedFin}
        </span>
      </div>

      {!loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: '0.85rem' }}>Aucune commission sur la période</div>
      )}

      {rows.length > 0 && (<>
        {/* ── KPI cards ── */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <KpiCard label="Nb entrées"        value={kpi.nb}                   color="#4a505c" />
          <KpiCard label="Commission active"  value={fmtM(kpi.commission)}    color="#2e7d32" />
          <KpiCard label="Commission init"   value={fmtM(kpi.commissionInit)} color="#1565c0" />
          <KpiCard label="Nb annulées"       value={kpi.nbAnnule}             color="#c62828" />
        </div>

        {/* ── Filtre annulé / OD ── */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600 }}>Statut :</span>
          {[
            { val: 'actif',  label: 'Actif',  color: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' },
            { val: 'annule', label: 'Annulé', color: '#c62828', bg: '#ffebee', border: '#ef9a9a' },
          ].map(opt => {
            const active = cancelFilter === opt.val;
            return (
              <button key={opt.val} onClick={() => { setCancelFilter(prev => prev === opt.val ? null : opt.val); setPage(1); }}
                style={{ background: active ? opt.color : opt.bg, color: active ? '#fff' : opt.color, border: `1.5px solid ${active ? opt.color : opt.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
                {opt.label}
              </button>
            );
          })}
          <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginLeft: 8 }}>OD :</span>
          {[
            { val: 'od',     label: 'OD uniquement', color: '#6a1b9a', bg: '#f3e5f5', border: '#ce93d8' },
            { val: 'normal', label: 'Hors OD',       color: '#264061', bg: '#eef2f7', border: '#b8c8da' },
          ].map(opt => {
            const active = odFilter === opt.val;
            return (
              <button key={opt.val} onClick={() => { setOdFilter(prev => prev === opt.val ? null : opt.val); setPage(1); }}
                style={{ background: active ? opt.color : opt.bg, color: active ? '#fff' : opt.color, border: `1.5px solid ${active ? opt.color : opt.border}`, borderRadius: 20, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s' }}>
                {opt.label}
              </button>
            );
          })}
          <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: '#aaa' }}>{filtered.length} entrée{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        {/* ── Chips type ── */}
        {typeChips.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Type :</span>
            {typeChips.map(c => {
              const active = typeFilter.has(c.type);
              const cat    = getTypeCat(c.label);
              return (
                <button key={c.type} onClick={() => toggleSet(setTypeFilter, c.type)}
                  style={{ background: active ? cat.color : cat.bg, color: active ? '#fff' : cat.color, border: `1.5px solid ${active ? cat.color : cat.border}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  {cat.prefix && <span style={{ fontSize: '0.7rem', opacity: active ? 1 : 0.7 }}>{cat.prefix}</span>}
                  {c.label}
                  <span style={{ fontSize: '0.6rem', opacity: 0.7 }}>({c.count})</span>
                </button>
              );
            })}
            {typeFilter.size > 0 && (
              <button onClick={() => { setTypeFilter(new Set()); setPage(1); }}
                style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
                <FaTimes size={10} />
              </button>
            )}
          </div>
        )}

        {/* ── Chips projet ── */}
        {allProjets.length > 1 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Projets :</span>
            {projetChips.map(p => {
              const active = projetFilter.has(p.nom);
              return (
                <button key={p.nom} onClick={() => toggleSet(setProjetFilter, p.nom)}
                  style={{ background: active ? '#4a505c' : '#f5f4f2', color: active ? '#fff' : '#4a505c', border: `1.5px solid ${active ? '#4a505c' : '#d4c8bc'}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                  {p.nom}
                  <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.7 }}>({p.count})</span>
                </button>
              );
            })}
            <button onClick={() => setProjetFilter(new Set(projetChips.map(p => p.nom)))} title="Tout sélectionner"
              style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#2e7d32', display: 'flex', alignItems: 'center' }}>
              <FaCheck size={10} />
            </button>
            <button onClick={() => { setProjetFilter(new Set()); setPage(1); }} title="Tout désélectionner"
              style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
              <FaTimes size={10} />
            </button>
          </div>
        )}

        {/* ── Chips commercial ── */}
        {comChips.length > 0 && (
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: '0.65rem', color: '#aaa', fontWeight: 600, marginRight: 2 }}>Commercial :</span>
            {comChips.map(c => {
              const active = comFilter.has(c.comId);
              return (
                <button key={c.comId} onClick={() => toggleSet(setComFilter, c.comId)}
                  style={{ background: active ? '#1565c0' : '#e3f2fd', color: active ? '#fff' : '#1565c0', border: `1.5px solid ${active ? '#1565c0' : '#90caf9'}`, borderRadius: 20, padding: '3px 10px', fontSize: '0.68rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s' }}>
                  {c.label}
                  <span style={{ marginLeft: 5, fontSize: '0.6rem', opacity: 0.7 }}>({c.count})</span>
                </button>
              );
            })}
            {comFilter.size > 0 && (
              <button onClick={() => { setComFilter(new Set()); setPage(1); }}
                style={{ background: 'none', border: '1px solid #d4c8bc', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', color: '#c62828', display: 'flex', alignItems: 'center' }}>
                <FaTimes size={10} />
              </button>
            )}
          </div>
        )}

        {/* ── Toggle vue + exports ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6, marginBottom: 12 }}>
          {['resume','detail'].map(v => (
            <button key={v} onClick={() => { setViewMode(v); setPage(1); }}
              style={{ background: viewMode === v ? '#4a505c' : '#f5f4f2', color: viewMode === v ? '#fff' : '#4a505c', border: '1.5px solid #d4c8bc', borderRadius: 6, padding: '3px 12px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}>
              {v === 'resume' ? 'Résumé' : 'Détail'}
            </button>
          ))}
          <TableExportBar
            onExcel={() => exportExcel(filtered.map(r => ({
              'ID': r.id, 'Contrat': r.venteId || '', 'Date': r.createdAt ? fmtDate(r.createdAt) : '', 'Type': r.description || r.type,
              'Projet': r.projet, 'Commercial': r.commercial_nom, 'Désignation': r.designation,
              'Type vente': r.typeVente, 'Prix net': r.prix_net, 'Enc montant': r.enc_montant,
              'Enc statut': r.encaissementStatut, 'Mode regl': r.mode_regl,
              'Commission': r.commission, 'Commission init': r.commission_init,
              'Formule': r.formula_name, 'OD': r.isOD ? 'Oui' : 'Non',
              'Annulé': r.isCancelled ? 'Oui' : 'Non', 'Annulé le': r.cancelledAt ? fmtDate(r.cancelledAt) : '',
            })), 'commissions')}
            onCopy={() => {
              const { html, plain } = rowsToHtml(filtered.map(r => ({
                'ID': r.id, 'Date': r.createdAt ? fmtDate(r.createdAt) : '', 'Type': r.description || r.type,
                'Projet': r.projet, 'Commercial': r.commercial_nom, 'Désignation': r.designation,
                'Commission': fmtM(r.commission), 'Commission init': fmtM(r.commission_init),
                'Annulé': r.isCancelled ? 'Oui' : 'Non',
              })));
              copyHtmlToClipboard(html, plain);
            }}
          />
        </div>

        {/* ══ VUE RÉSUMÉ ══ */}
        {viewMode === 'resume' && (
          <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ ...TH, textAlign: 'left', borderRight: '2px solid #d4c8bc' }}>Commercial</th>
                    <th style={{ ...TH, textAlign: 'right' }}>Nb total</th>
                    <th style={{ ...TH, textAlign: 'right', color: '#2e7d32' }}>Commission</th>
                    <th style={{ ...TH, textAlign: 'right', color: '#1565c0' }}>Commission init</th>
                    <th style={{ ...TH, textAlign: 'right', color: '#2e7d32' }}>Nb actif</th>
                    <th style={{ ...TH, textAlign: 'right', color: '#c62828', borderLeft: '2px solid #d4c8bc' }}>Nb annulé</th>
                  </tr>
                </thead>
                <tbody>
                  {resumeRows.map((r, i) => (
                    <tr key={r.comId} style={{ background: i % 2 === 0 ? '#fff' : '#f9f8f7' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                      onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#f9f8f7'}>
                      <td style={{ ...TD, fontWeight: 700, color: '#4a505c', borderRight: '2px solid #d4c8bc' }}>{r.commercial}</td>
                      <td style={{ ...TD, textAlign: 'right' }}>{r.nb}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{r.commission > 0 ? fmtM(r.commission) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#1565c0' }}>{r.commissionInit > 0 ? fmtM(r.commissionInit) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#2e7d32' }}>{r.nbActif}</td>
                      <td style={{ ...TD, textAlign: 'right', color: r.nbAnnule > 0 ? '#c62828' : '#888', borderLeft: '2px solid #d4c8bc' }}>{r.nbAnnule > 0 ? r.nbAnnule : '—'}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #d4c8bc', background: '#f5f4f2' }}>
                    <td style={{ ...TD, fontWeight: 700, color: '#4a505c', borderRight: '2px solid #d4c8bc' }}>TOTAL</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700 }}>{kpi.nb}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{fmtM(kpi.commission)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#1565c0' }}>{fmtM(kpi.commissionInit)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{resumeRows.reduce((s,r)=>s+r.nbActif,0)}</td>
                    <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#c62828', borderLeft: '2px solid #d4c8bc' }}>{kpi.nbAnnule || '—'}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* ══ VUE DÉTAIL ══ */}
        {viewMode === 'detail' && (
          <div style={{ borderRadius: 6, border: '2px solid #d4c8bc', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.74rem' }}>
                <thead>
                  <tr>
                    <ThS label="ID"          field="id" />
                    <ThS label="Contrat"     field="venteId" />
                    <ThS label="Date"        field="createdAt" />
                    <ThS label="Type"        field="type" />
                    <ThS label="Projet"      field="projet" />
                    <ThS label="Commercial"  field="commercial_nom" />
                    <ThS label="Désignation" field="designation" />
                    <ThS label="Type vente"  field="typeVente" />
                    <ThS label="Prix net"    field="prix_net"          right />
                    <ThS label="Enc montant" field="enc_montant"       right />
                    <ThS label="Enc statut"  field="encaissementStatut" />
                    <ThS label="Mode regl"   field="mode_regl" />
                    <ThS label="Commission"  field="commission"        right />
                    <ThS label="Com. init"   field="commission_init"   right />
                    <ThS label="Formule"     field="formula_name" />
                    <th style={{ ...TH }}>OD</th>
                    <th style={{ ...TH }}>Statut</th>
                    <ThS label="Annulé le"   field="cancelledAt" />
                  </tr>
                </thead>
                <tbody>
                  {pagedRows.map((r, i) => (
                    <tr key={r.id}
                      style={{ background: r.isCancelled ? '#fff8f8' : (i % 2 === 0 ? '#fff' : '#f9f8f7') }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0ede8'}
                      onMouseLeave={e => e.currentTarget.style.background = r.isCancelled ? '#fff8f8' : (i % 2 === 0 ? '#fff' : '#f9f8f7')}>
                      <td style={{ ...TD, textAlign: 'center', color: '#888', fontSize: '0.68rem' }}>{r.id}</td>
                      <td style={{ ...TD, textAlign: 'center', whiteSpace: 'nowrap' }}>
                        {r.venteId
                          ? <a href={`https://shems.ngrok.io/consultation/${r.venteId}`} target="_blank" rel="noreferrer"
                              style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>
                              #{r.venteId}
                            </a>
                          : '—'}
                      </td>
                      <td style={{ ...TD, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>{r.createdAt ? fmtDate(r.createdAt) : '—'}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                        {(() => { const cat = getTypeCat(r.description || r.type || ''); return (
                          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: cat.color, background: cat.bg, border: `1px solid ${cat.border}`, borderRadius: 10, padding: '2px 7px', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            {cat.prefix && <span>{cat.prefix}</span>}{r.description || r.type || '—'}
                          </span>
                        ); })()}
                      </td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.projet || '—'}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap', fontWeight: 600 }}>{r.commercial_nom || '—'}</td>
                      <td style={{ ...TD, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.designation || '—'}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap' }}>{r.typeVente || '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#4a505c' }}>{r.prix_net > 0 ? fmt(r.prix_net) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#4a505c' }}>{r.enc_montant > 0 ? fmt(r.enc_montant) : '—'}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>{r.encaissementStatut || '—'}</td>
                      <td style={{ ...TD, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>{r.mode_regl || '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#2e7d32', fontWeight: 600 }}>{r.commission != null ? fmt(r.commission) : '—'}</td>
                      <td style={{ ...TD, textAlign: 'right', color: '#1565c0' }}>{r.commission_init != null ? fmt(r.commission_init) : '—'}</td>
                      <td style={{ ...TD, fontSize: '0.68rem' }}>{r.formula_name || '—'}</td>
                      <td style={{ ...TD, textAlign: 'center' }}>
                        {r.isOD ? <span style={{ fontSize: '0.65rem', fontWeight: 700, color: '#6a1b9a', background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 10, padding: '2px 6px' }}>OD</span> : '—'}
                      </td>
                      <td style={{ ...TD }}><CancelBadge isCancelled={r.isCancelled} /></td>
                      <td style={{ ...TD, whiteSpace: 'nowrap', fontSize: '0.68rem', color: '#c62828' }}>{r.cancelledAt ? fmtDate(r.cancelledAt) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sorted.length > PAGE_SIZE && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 12px', borderTop: '1px solid #ede9e4', background: '#faf9f7' }}>
                <span style={{ fontSize: '0.72rem', color: '#aaa' }}>
                  <span style={{ color: '#4a505c', fontWeight: 600 }}>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, sorted.length)}</span> sur {sorted.length}
                </span>
                <div style={{ display: 'flex', gap: 3 }}>
                  {[{ l: '«', t: 1 }, { l: '‹', t: page - 1 }, { l: '›', t: page + 1 }, { l: '»', t: totalPages }].map(({ l, t }) => (
                    <button key={l} onClick={() => setPage(Math.max(1, Math.min(totalPages, t)))}
                      disabled={t < 1 || t > totalPages || t === page}
                      style={{ minWidth: 26, height: 24, border: '1px solid #d4c8bc', borderRadius: 5, background: t === page ? '#4a505c' : '#fff', color: t === page ? '#fff' : '#4a505c', cursor: t < 1 || t > totalPages ? 'default' : 'pointer', fontSize: '0.72rem' }}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </>)}
    </div>
  );
};

export default CommissionSection;
